import { Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { isBlogCategorySlug, isBlogHubSlug } from './core/api/blog-slug-registry';
import { shopEnabledGuard } from './core/shop/shop-enabled.guard';
import { isDiscountHubSuffix, matchesDiscountSuffix } from './core/api/discount-type-registry';
import {
  ACCESSIBILITY_STATEMENT,
  AFFILIATE_DISCLOSURE,
  COOKIE_POLICY,
  PRIVACY_POLICY,
  TERMS_OF_SERVICE,
} from './features/legal/legal-content';
import { HomePage } from './features/home/home-page/home-page';
import { RootSlugPage } from './features/root-slug/root-slug-page';
import { StorePage } from './features/store/store-page/store-page';
import { NotFoundPage } from './features/not-found/not-found-page/not-found-page';
import { MainLayout } from './layouts/main-layout/main-layout';
import { AboutPage } from './features/about/about-page/about-page';
import { AddYourBrandPage } from './features/add-your-brand/add-your-brand-page/add-your-brand-page';
import { AllstoresPage } from './features/allstores/allstores-page/allstores-page';
import { BlogAuthorDetailPage } from './features/blog/pages/blog-author-detail-page/blog-author-detail-page';
import { BlogAuthorsPage } from './features/blog/pages/blog-authors-page/blog-authors-page';
import { BlogCategoriesPage } from './features/blog/pages/blog-categories-page/blog-categories-page';
import { BlogCategoryDetailPage } from './features/blog/pages/blog-category-detail-page/blog-category-detail-page';
import { BlogDetailPage } from './features/blog/pages/blog-detail-page/blog-detail-page';
import { BlogHomePage } from './features/blog/pages/blog-home-page/blog-home-page';
import { BlogSilosHubPage } from './features/blog/pages/blog-silos-hub-page/blog-silos-hub-page';
import { BlogSilosPage } from './features/blog/pages/blog-silos-page/blog-silos-page';
import { CategoriesPage } from './features/categories/categories-page/categories-page';
import { CategoryDetailPage } from './features/categories/category-detail-page/category-detail-page';
import { CompareHubPage } from './features/compare/compare-hub-page/compare-hub-page';
import { ComparePage } from './features/compare/compare-page/compare-page';
import { ContactPage } from './features/contact/contact-page/contact-page';
import { CouponCollectionPage } from './features/coupon-collection/coupon-collection-page/coupon-collection-page';
import { CouponsHubPage } from './features/coupon-collection/coupons-hub-page/coupons-hub-page';
import { DealsCategoryPage } from './features/deals/deals-category-page/deals-category-page';
import { DealsEventPage } from './features/deals/deals-event-page/deals-event-page';
import { DealsPage } from './features/deals/deals-page/deals-page';
import { DealsStorePage } from './features/deals/deals-store-page/deals-store-page';
import { DiscountHubPage } from './features/discount-page/discount-hub-page/discount-hub-page';
import { DiscountPage } from './features/discount-page/discount-page';
import { EventDetailPage } from './features/events/event-detail-page/event-detail-page';
import { EventsPage } from './features/events/events-page/events-page';
import { HowVerificationWorksPage } from './features/how-verification-works/how-verification-works-page/how-verification-works-page';
import { LegalPage } from './features/legal/legal-page/legal-page';
import { SubmitCouponPage } from './features/submit-coupon/submit-coupon-page/submit-coupon-page';
import { WhyTrustUsPage } from './features/why-trust-us/why-trust-us-page/why-trust-us-page';

/**
 * Matches a store discount page: `<store>-student-discount`, `-teacher-discount`, and any other
 * suffix an admin has since added.
 *
 * A matcher rather than a static path because the slug is generated per store, so the set of URLs
 * cannot be listed here. It must also be registered BEFORE `:storeSlug`, which is a single-segment
 * catch-all and would otherwise claim these and render the store page instead.
 *
 * The valid suffixes are looked up at runtime (see discount-type-registry) rather than hardcoded,
 * because discount types are admin-managed. Matching any `*-discount` URL instead would be wrong:
 * ~1.3k real store slugs end that way, and all of them would stop resolving.
 */
export function discountPageMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length !== 1) return null;

  const slug = segments[0].path;
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return null;
  if (!matchesDiscountSuffix(slug)) return null;

  return { consumed: segments, posParams: { discountSlug: segments[0] } };
}

/**
 * Matches a discount type's hub: `/student-discount`, `/teacher-discount`, and any type added since.
 *
 * The URL is exactly the type's suffix with no store in front, which is what separates it from
 * `discountPageMatcher` above. Registered before it for readability; the two cannot both match the
 * same URL.
 */
export function discountHubMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length !== 1) return null;

  const slug = segments[0].path;
  if (!isDiscountHubSuffix(slug)) return null;

  return { consumed: segments, posParams: { hubSlug: segments[0] } };
}

/**
 * Matches a store page, with or without the coupon-modal segment:
 *   /<storeSlug>
 *   /<storeSlug>/coupon/<couponId>
 *
 * Deliberately ONE route rather than two. Two separate route definitions pointing at the same
 * component still count as different routes, so Angular destroys and re-creates `StorePage` when
 * the modal opens — refetching the whole store and discarding the coupons the modal needs. A single
 * matcher keeps it the same activated route, so opening the modal is just a param change.
 *
 * Registered last (before `**`), so every earlier route — /blogs/*, /compare/*, the discount
 * matchers — still wins.
 */
/**
 * Everything under the retired `/redirect/` and `/redirect_p/` space, at any depth.
 *
 * A matcher rather than a list of paths because the old system emitted more shapes than anyone
 * wrote down. `/redirect/<id>` and `/redirect/<id>/<offer>` cover the bulk — 1,440 requests in a
 * two-and-a-half hour log — but `/redirect/<id>/img/product<hash>.webp` also still arrives, and it
 * was answering 404 while its siblings answered 410. That difference matters: 404 means
 * not-found-right-now and invites a recrawl, 410 means permanently gone and drops the URL. Leaving
 * one shape on 404 keeps that corner of the index alive indefinitely.
 *
 * Consuming the whole segment list means no further variant can appear and quietly land on 404.
 */
export function retiredRedirectMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (!segments.length) return null;
  const head = segments[0].path;
  if (head !== 'redirect' && head !== 'redirect_p') return null;
  return { consumed: segments };
}

export function storePageMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length === 1) {
    // Both names for the same segment. A one-segment URL can be a store or a product, and which
    // page renders is decided at runtime by RootSlugPage — but the two pages read different param
    // names (`storeSlug` / `productSlug`) because each also has its own route. Exposing both means
    // neither page needs to know how it was reached.
    return {
      consumed: segments,
      posParams: { storeSlug: segments[0], productSlug: segments[0] },
    };
  }

  if (segments.length === 3 && segments[1].path === 'coupon') {
    return {
      consumed: segments,
      posParams: { storeSlug: segments[0], couponId: segments[2] },
    };
  }

  return null;
}

/**
 * Matches a blog category at its new URL: `/blogs/<categorySlug>`.
 *
 * Blog categories and hubs share this shape (`/blogs/coupon-strategy` vs `/blogs/smart-shopping-
 * hub`), so which component to render can only be decided by looking the slug up — see
 * `blog-slug-registry`, which an app initializer fills before the first navigation.
 *
 * Registered AFTER every fixed `/blogs/...` route, so `/blogs/categories`, `/blogs/hubs`,
 * `/blogs/...` fixed routes still win.
 */
export function blogCategoryMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length !== 2 || segments[0].path !== 'blogs') return null;
  if (!isBlogCategorySlug(segments[1].path)) return null;

  return { consumed: segments, posParams: { categorySlug: segments[1] } };
}

/** The same for a hub (silo): `/blogs/<hubSlug>`. */
export function blogHubMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length !== 2 || segments[0].path !== 'blogs') return null;
  if (!isBlogHubSlug(segments[1].path)) return null;

  return { consumed: segments, posParams: { siloSlug: segments[1] } };
}

export const routes: Routes = [
  {
    path: '',
    component: MainLayout,
    children: [
      { path: '', component: HomePage },
      { path: 'about-us', component: AboutPage },
      { path: 'add-your-brand', component: AddYourBrandPage },
      { path: 'contact-us', component: ContactPage },
      { path: 'why-trust-us', component: WhyTrustUsPage },
      // One component, two documents — the page it renders comes from `data`.
      { path: 'privacy-policy', component: LegalPage, data: { document: PRIVACY_POLICY } },
      { path: 'cookie-policy', component: LegalPage, data: { document: COOKIE_POLICY } },
      { path: 'terms', component: LegalPage, data: { document: TERMS_OF_SERVICE } },
      { path: 'affiliate-disclosure', component: LegalPage, data: { document: AFFILIATE_DISCLOSURE } },
      { path: 'accessibility', component: LegalPage, data: { document: ACCESSIBILITY_STATEMENT } },
      { path: 'how-verification-works', component: HowVerificationWorksPage },
      { path: 'submit-coupon', component: SubmitCouponPage },
      { path: 'categories', component: CategoriesPage },
      { path: 'categories/:categorySlug', component: CategoryDetailPage },
      { path: 'events', component: EventsPage },
      { path: 'event/:eventslug', component: EventDetailPage },
      { path: 'storelist/:letter', component: AllstoresPage },
      { path: 'storelist', component: AllstoresPage },
      { path: 'blogs', component: BlogHomePage },
      // The standalone "All posts" listing was removed — `/blogs` is the blog index. Retired, not
      // redirected: it answers 410 so the old URL leaves the index rather than living on as a
      // redirect. Same treatment as the `deals/*` URLs below and `shop/products`.
      { path: 'blogs/posts', component: NotFoundPage, pathMatch: 'full', data: { gone: true } },
      { path: 'blogs/categories', component: BlogCategoriesPage },
      // Hubs (formerly "silos"). The old `/blogs/silos*` and `/blogs/categories/<slug>` URLs are
      // gone outright rather than redirected — the new URL is the only one.
      { path: 'blogs/hubs', component: BlogSilosHubPage },
      { path: 'experts', component: BlogAuthorsPage },
      { path: 'experts/:authorSlug', component: BlogAuthorDetailPage },
      { path: 'blogs/experts', component: BlogAuthorsPage },
      { path: 'blogs/experts/:authorSlug', component: BlogAuthorDetailPage },
      // `/blogs/<slug>` — a category or a hub, resolved by the matchers above. These must stay
      // BELOW every fixed `/blogs/...` route.
      { matcher: blogCategoryMatcher, component: BlogCategoryDetailPage },
      { matcher: blogHubMatcher, component: BlogSilosPage },
      // Articles live at the same `/blogs/<slug>` shape. This is a plain route rather than a third
      // registry-backed matcher: whatever the two matchers above did not claim is an article, and
      // the page 404s on its own if no such article exists. That way 233+ article slugs never have
      // to be shipped to the client just to resolve a URL.
      { path: 'blogs/:slug', component: BlogDetailPage },
      // A product now lives at the bare `/<productSlug>`, beside stores. The old address is retired
      // outright rather than redirected, the same treatment as `deals/category/*` above and for the
      // same reason: a 410 drops it from the index instead of keeping it alive as a redirect.
      {
        path: 'deals/product/:productSlug',
        component: NotFoundPage,
        pathMatch: 'full',
        data: { gone: true },
      },
      // A product collection: `/collection/<categorySlug>`. Two segments, so the single-segment
      // `:storeSlug` catch-all further down cannot claim it.
      { path: 'collection/:categorySlug', component: DealsCategoryPage },
      // One store within a collection: `/collection/<categorySlug>/<storeKey>`. Three segments, so
      // it cannot collide with the two-segment collection route above.
      { path: 'collection/:categorySlug/:storeName', component: DealsStorePage },
      // Earlier URLs for the same page. Retired outright: they answer 410 Gone rather than
      // redirecting to `/collection/:categorySlug`, so `/collection/...` is the only address for
      // this page and the old ones drop out of the index instead of being kept alive by a redirect.
      {
        path: 'deals/category/:categorySlug',
        component: NotFoundPage,
        pathMatch: 'full',
        data: { gone: true },
      },
      {
        path: 'deals/collection/:categorySlug',
        component: NotFoundPage,
        pathMatch: 'full',
        data: { gone: true },
      },
      // An event's DEALS page — the product side of an event. `/event/:eventslug` remains its coupon
      // page; this one lists products and links each store to its own deals page.
      { path: 'deals/event/:eventSlug', component: DealsEventPage },
      // Retired, like every other `deals/*` address above. It used to render the store's products
      // page a second time and then rewrite the address bar to `/collection/...`, so the page had
      // two live URLs and the second one existed only to bounce off.
      //
      // Nothing was relying on it: all 86 stores with a products page sit in a collection, so every
      // one of them already had the `/collection/<category>/<store>` address, and a full crawl of
      // the site found zero links to `/deals/<store>` and zero in the sitemaps.
      //
      // 410 rather than a redirect, for the reason the routes around it give: it drops out of the
      // index instead of being kept alive.
      {
        path: 'deals/:storeName',
        component: NotFoundPage,
        pathMatch: 'full',
        data: { gone: true },
      },
      // The deals landing page lives at `/collection`. Bare `/deals` is retired and answers 410 —
      // `pathMatch: 'full'` limits this to exactly `/deals`, so the deeper `/deals/product/*`,
      // `/deals/event/*` and `/deals/:storeName` routes above are untouched.
      { path: 'collection', component: DealsPage },
      { path: 'deals', component: NotFoundPage, pathMatch: 'full', data: { gone: true } },
      // The previous savingarena.com's outbound redirect scheme. Nothing on this site links to it —
      // no route, no sitemap entry, no internal anchor — yet it is by a wide margin the most-crawled
      // thing on the domain: 51,254 requests across 31,959 distinct URLs in fifteen hours of logs,
      // every one a 404, and 84% of every 404 the site serves. The traffic is external backlinks and
      // stale index entries, led by the backlink crawlers (serpstatbot, AhrefsBot, DotBot), with
      // Googlebot spending ~9% of its budget here.
      //
      // 410 rather than 404 for the reason the retired routes above give: 404 means not-found-right-now
      // and invites a recrawl; 410 means permanently gone and drops it. Deliberately NOT a robots.txt
      // block — that would stop the crawl but also stop the 410 ever being read, freezing 31,959 URLs
      // in the index as crawled-but-blocked.
      { matcher: retiredRedirectMatcher, component: NotFoundPage, data: { gone: true } },
      // Must stay ABOVE `:storeSlug`. `/compare/<slug>` is two segments so the single-segment store
      // catch-all can't swallow it, but keeping it here matches the `deals/*` precedent and makes the
      // ordering constraint obvious.
      // `/compare` must be declared before `/compare/:slug` and both before `:storeSlug`. The hub is
      // what the comparison breadcrumb ("Home / Comparisons / …") points at — without it that crumb,
      // and the BreadcrumbList schema built from it, resolved to a 404.
      { path: 'compare', component: CompareHubPage },
      { path: 'compare/:slug', component: ComparePage },
      // Switch-based coupon collections (verified / exclusive / free-shipping / student-discount).
      // `/coupons` is the hub; `/coupons/:collection` each list. Both above the `:storeSlug` catch-all.
      { path: 'coupons', component: CouponsHubPage },
      { path: 'coupons/:collection', component: CouponCollectionPage },
      // The Shop uses the same global MainLayout header/footer as the rest of the site. Lazy-loaded
      // so its bundle stays separate. Multi-segment Shop URLs (/shop/products, /shop/product/:slug…)
      // can't be caught by the single-segment `:storeSlug`, but this stays above it to match the
      // `deals/*` precedent and keep the ordering constraint obvious.
      {
        path: 'shop',
        canMatch: [shopEnabledGuard],
        loadChildren: () => import('./features/shop/shop.routes').then((m) => m.SHOP_ROUTES),
      },
      // With the Shop switched off in the admin the route above stops matching, and every `/shop`
      // URL lands here instead — a real 404, from the bare `/shop` down to `/shop/product/<slug>`.
      //
      // This catch-all is what makes that honest. Without it a switched-off `/shop` would fall
      // through to the single-segment `:storeSlug` matcher below and be looked up as a store slug,
      // and only the deeper Shop URLs would reach the site-wide `**`.
      //
      // A plain 404, not a 410: the Shop is switched off, not retired. Gone would tell Google the
      // URLs are permanently removed, which is exactly wrong for a switch an admin flips back.
      //
      // Two children rather than one `**`: the wildcard covers everything under `/shop`, and the
      // empty path covers the bare `/shop` itself.
      {
        path: 'shop',
        children: [
          { path: '', component: NotFoundPage },
          { path: '**', component: NotFoundPage },
        ],
      },
      { path: 'zaful.com', component: StorePage },
      // Store discount pages and their hubs must be tried before `:storeSlug` — see the matchers above.
      { matcher: discountHubMatcher, component: DiscountHubPage },
      { matcher: discountPageMatcher, component: DiscountPage },
      // One route for `/<store>`, `/<product>` and `/<store>/coupon/<id>` — see storePageMatcher.
      //
      // Still ONE route, for the reason given on the matcher: two routes pointing at the same page
      // make Angular destroy and re-create it when the coupon modal opens. RootSlugPage decides
      // which page a bare segment is and keeps it mounted across that param change.
      { matcher: storePageMatcher, component: RootSlugPage },
      // Multi-segment URLs that match nothing render a real 404 (with a 404 status from the SSR
      // server). This used to be `redirectTo: ''`, which turned every broken link into a 200
      // homepage — a soft 404, and an invitation for Google to index junk URLs as duplicates.
      { path: '**', component: NotFoundPage },
    ],
  },
];
